import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAyushBState } from "../interfaces/IPreAuth";
import { fetchAyushBClaimData, fetchAyushBClaimFinanceData, fetchDiagnosis, fetchDoctors, fetchImplants, fetchInvestigations, fetchPortalData, fetchProcedures, fetchSpecialities, fetchStratifications } from "../apis/ayushmanBService";

const initialState: IAyushBState = {
  portalData: null,
  preAuth: {
    specialities: [],
    procedures: [],
    stratifications: [],
    doctors: [],
    diagnosis: [],
    investigations: [],
    implants: [],
  },
  claim: {
    caseData: null,
    selectedTreatmentPlans: [],
    postInvestigations: [],
    financeDetails: null
  },
  isFetchingData: false
};

export const getPortalDataForAyushB = createAsyncThunk(
  "ayushBPreAuth/getPortalDataForAyushB",
  async ({ caseId, visitId }: { caseId: number; visitId: number }) => {
    const response = await fetchPortalData(caseId, visitId);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getSpeacialities = createAsyncThunk(
  "ayushBPreAuth/getSpeacialities",
  async ({ portalMetaDataId }: { portalMetaDataId: number }) => {
    const response = await fetchSpecialities(portalMetaDataId);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getProcedures = createAsyncThunk(
  "ayushBPreAuth/getProcedures",
  async ({ portalMetaDataId }: { portalMetaDataId: number }) => {
    const response = await fetchProcedures(portalMetaDataId);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getStratifications = createAsyncThunk(
  "ayushBPreAuth/getStratification",
  async ({ portalMetaDataId, procedureCode }: { portalMetaDataId: number, procedureCode: string }) => {
    const response = await fetchStratifications(portalMetaDataId, procedureCode);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getImplants = createAsyncThunk(
  "ayushBPreAuth/getImplants",
  async ({ portalMetaDataId, data }: { portalMetaDataId: number, data: any }) => {
    const response = await fetchImplants(portalMetaDataId, data);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getInvestigations = createAsyncThunk(
  "ayushBPreAuth/getInvestigations",
  async ({ portalMetaDataId, data }: { portalMetaDataId: number, data: any }) => {
    const response = await fetchInvestigations(portalMetaDataId, data);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getDoctors = createAsyncThunk(
  "ayushBPreAuth/getDoctors",
  async ({ portalMetaDataId, data }: { portalMetaDataId: number, data: any }) => {
    const response = await fetchDoctors(portalMetaDataId, data);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getDiagnosis = createAsyncThunk(
  "ayushBPreAuth/getDiagnosis",
  async ({ portalMetaDataId, searchTerm }: { portalMetaDataId: number, searchTerm: string }) => {
    const response = await fetchDiagnosis(portalMetaDataId, searchTerm);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

// Claim
export const getClaimDataForAyushB = createAsyncThunk(
  "ayushBClaim/getClaimDataForAyushB",
  async ({ caseId, visitId, portalMetaDataId }: { caseId: number; visitId: number, portalMetaDataId: number }) => {
    const response = await fetchAyushBClaimData(caseId, visitId, portalMetaDataId);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

export const getClaimFinanceForAyushB = createAsyncThunk(
  "ayushBClaim/getClaimFinanceForAyushB",
  async ({ claimId }: {claimId: number }) => {
    const response = await fetchAyushBClaimFinanceData(claimId);
    if (typeof response === "object") {
      return response;
    } else {
      return null;
    }
  }
);

const AyushBPreAuthSlice = createSlice({
  name: "ayushBPreAuth",
  initialState: initialState,
  reducers: {
    resetAyushState: () => initialState
  },

  extraReducers: (builder) => {
    builder
      .addCase(getPortalDataForAyushB.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getPortalDataForAyushB.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.portalData = action.payload;
      })
      .addCase(getPortalDataForAyushB.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getClaimDataForAyushB.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getClaimDataForAyushB.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.claim.caseData = action.payload;
      })
      .addCase(getClaimDataForAyushB.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getSpeacialities.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getSpeacialities.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.specialities = action.payload;
      })
      .addCase(getSpeacialities.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getProcedures.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getProcedures.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.procedures = action.payload;
      })
      .addCase(getProcedures.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getStratifications.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getStratifications.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.stratifications = action.payload;
      })
      .addCase(getStratifications.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getDoctors.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getDoctors.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.doctors = action.payload;
      })
      .addCase(getDoctors.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(getDiagnosis.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getDiagnosis.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.diagnosis = action.payload || [];
      })
      .addCase(getDiagnosis.rejected, (state) => {
        state.isFetchingData = false;
        state.preAuth.diagnosis = [];
      })
      .addCase(getInvestigations.pending, (state) => {
        state.isFetchingData = true;
        // Update the preAuthUrlLink property with the new URL from the action payload
      })
      .addCase(getInvestigations.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.investigations = action.payload || [];
      })
      .addCase(getInvestigations.rejected, (state) => {
        state.isFetchingData = false;
        state.preAuth.investigations = [];
      })
      .addCase(getImplants.pending, (state) => {
        state.isFetchingData = true;
      })
      .addCase(getImplants.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.preAuth.implants = action.payload || [];
      })
      .addCase(getImplants.rejected, (state) => {
        state.isFetchingData = false;
        state.preAuth.implants = [];
      })
      .addCase(getClaimFinanceForAyushB.pending, (state) => {
        state.isFetchingData = true;
      })
      .addCase(getClaimFinanceForAyushB.fulfilled, (state, action: PayloadAction<any>) => {
        state.isFetchingData = false;
        state.claim.financeDetails = action.payload || [];
      })
      .addCase(getClaimFinanceForAyushB.rejected, (state) => {
        state.isFetchingData = false;
        state.claim.financeDetails = null;
      })

  }
});

export const {
  resetAyushState
} = AyushBPreAuthSlice.actions;
export default AyushBPreAuthSlice.reducer;